import {
  getPageLayout,
  rectGeometry,
} from 'features/knowledgeDocumentViewer/components/viewer/search/aliasHighlightGeometry'
import type { DisplayRect } from 'features/knowledgeDocumentViewer/components/viewer/shared/liveValueDisplayRect'
import type { PlaceableChip } from 'features/knowledgeDocumentViewer/components/viewer/shared/resolveChipOverlaps'
import type { Box } from 'features/knowledgeDocumentViewer/components/viewer/shared/types'
import { useDocumentTabDataContextOptional } from 'features/knowledgeDocumentViewer/contexts/DocumentTabDataContext'
import { useKnowledgeDocumentUXContextOptional } from 'features/knowledgeDocumentViewer/contexts/KnowledgeDocumentUXContext'
import {
  formatLiveProcessValue,
  type LiveValueDetailTier,
  useLiveProcessDataValues,
} from 'features/knowledgeDocumentViewer/hooks/useLiveProcessDataValues'
import type { AliasHighlight } from 'features/knowledgeDocumentViewer/types/documentSubjects'
import { VIEWER_PREFERENCES_DEFAULTS } from 'features/knowledgeDocumentViewer/utils/viewerPreferencesStorage'
import { useGeckoPermissions } from 'features/permissions'
import { useMemo } from 'react'
import { PERMISSION_CANTILEVER_POWER_FIM_VIEWER } from 'types'

const BOX_EXPAND_BY = 2
const BOX_SHIFT_X = 1
const BOX_SHIFT_Y = 0
const MIN_CHIPS_TO_NORMALIZE = 4

function median(values: number[]): number {
  const sorted = [...values].sort((a, b) => a - b)
  const mid = Math.floor(sorted.length / 2)
  return sorted.length % 2 === 0
    ? (sorted[mid - 1] + sorted[mid]) / 2
    : sorted[mid]
}

function populationStdDev(values: number[], mean: number): number {
  const variance =
    values.reduce((sum, value) => sum + (value - mean) ** 2, 0) / values.length
  return Math.sqrt(variance)
}

/** Snaps a chip's width and height to the page's median when either strays more than one standard deviation from it, keeping the chip's center fixed. */
function normalizeChipSizes(chips: LiveValueChip[]): LiveValueChip[] {
  if (chips.length < MIN_CHIPS_TO_NORMALIZE) return chips

  const widths = chips.map((chip) => chip.width)
  const heights = chips.map((chip) => chip.height)
  const medianW = median(widths)
  const medianH = median(heights)
  const sdW = populationStdDev(widths, medianW)
  const sdH = populationStdDev(heights, medianH)

  return chips.map((chip) => {
    const normalizeWidth = Math.abs(chip.width - medianW) > sdW
    const normalizeHeight = Math.abs(chip.height - medianH) > sdH
    if (!normalizeWidth && !normalizeHeight) return chip

    const centerX = chip.x + chip.width / 2
    const centerY = chip.y + chip.height / 2
    const width = normalizeWidth ? medianW : chip.width
    const height = normalizeHeight ? medianH : chip.height
    return {
      ...chip,
      x: normalizeWidth ? centerX - width / 2 : chip.x,
      y: normalizeHeight ? centerY - height / 2 : chip.y,
      width,
      height,
    }
  })
}

/** What a chip sits on, so it can hand hover and clicks back to it. */
export type LiveChipSource =
  | { kind: 'box'; box: Box }
  | { kind: 'alias'; highlight: AliasHighlight }

export type LiveChipPayload = {
  label: string
  text: string
  stale: boolean
  source: LiveChipSource
}

export type LiveValueChip = PlaceableChip<LiveChipPayload>

type UseLiveValueChipsArgs = {
  boxes: Box[]
  imageNatural: { w: number; h: number }
  firstPageWidth?: number
  isMultipage?: boolean
  detailTier?: LiveValueDetailTier
}

/**
 * Value chips for a page, sized to their anchor box (after outlier-size
 * normalization) and left free to overlap one another just like the boxes.
 *
 * Chips are built as one set rather than per box: the dedup pass still has to
 * compare chips whose anchor boxes overlap or duplicate one another. Both the
 * OCR boxes and the subject highlights anchor a reading, and only the boxes
 * answer to the OCR-box filter, so without the highlights hiding boxes would
 * take the readings with them.
 *
 * `chipRectByBoxId` lets callers anchor a box's tooltip to its chip's rect,
 * which reflects the box after normalization.
 */
export function useLiveValueChips({
  boxes,
  imageNatural,
  firstPageWidth,
  isMultipage,
  detailTier = 3,
}: UseLiveValueChipsArgs): {
  chips: LiveValueChip[]
  chipRectByBoxId: Map<string, DisplayRect>
} {
  const { hasPermissions } = useGeckoPermissions()
  const canSeeLiveProcessData = hasPermissions([
    PERMISSION_CANTILEVER_POWER_FIM_VIEWER,
  ])
  const showLiveProcessData =
    useKnowledgeDocumentUXContextOptional()?.showLiveProcessData ??
    VIEWER_PREFERENCES_DEFAULTS.showLiveProcessData
  const tabData = useDocumentTabDataContextOptional()
  const hasTimeSeriesData = tabData?.hasTimeSeriesData
  const aliasHighlights = tabData?.aliasHighlights
  const pageNumber = tabData?.pageNumber

  const anchors = useMemo(() => {
    if (!canSeeLiveProcessData || !showLiveProcessData || !hasTimeSeriesData)
      return []

    const fromBoxes = boxes.flatMap((box) =>
      box.label && hasTimeSeriesData(box.label)
        ? [
            {
              key: box.id,
              label: box.label,
              source: { kind: 'box', box } as LiveChipSource,
              x: box.x - BOX_EXPAND_BY + BOX_SHIFT_X,
              y: box.y - BOX_EXPAND_BY + BOX_SHIFT_Y,
              width: box.w + BOX_EXPAND_BY * 2,
              height: box.h + BOX_EXPAND_BY * 2,
            },
          ]
        : []
    )

    const fromHighlights = (aliasHighlights ?? []).flatMap(
      (highlight, index) => {
        const onPage =
          highlight.page === pageNumber ||
          (isMultipage && highlight.page === (pageNumber ?? 0) + 1)
        if (!onPage || !hasTimeSeriesData(highlight.label)) return []

        const { pageWidth, xOffset } = getPageLayout(
          isMultipage === true && highlight.page > (pageNumber ?? 0),
          imageNatural,
          firstPageWidth,
          isMultipage
        )
        return [
          {
            key: `alias-${highlight.subjectUid}-${highlight.aliasNorm}-${index}`,
            label: highlight.label,
            source: { kind: 'alias', highlight } as LiveChipSource,
            ...rectGeometry(highlight.bbox, pageWidth, xOffset, imageNatural.h),
          },
        ]
      }
    )

    return [...fromBoxes, ...fromHighlights]
  }, [
    aliasHighlights,
    boxes,
    canSeeLiveProcessData,
    firstPageWidth,
    hasTimeSeriesData,
    imageNatural,
    isMultipage,
    pageNumber,
    showLiveProcessData,
  ])

  const labels = useMemo(() => anchors.map((anchor) => anchor.label), [anchors])
  const liveValues = useLiveProcessDataValues(labels)

  return useMemo(() => {
    const chips: LiveValueChip[] = anchors.flatMap((anchor) => {
      const live = liveValues.get(anchor.label)
      if (!live) return []
      const text = formatLiveProcessValue(live, detailTier)
      return [
        {
          key: anchor.key,
          payload: {
            label: anchor.label,
            text,
            stale: live.isStale,
            source: anchor.source,
          },
          x: anchor.x,
          y: anchor.y,
          width: anchor.width,
          height: anchor.height,
        },
      ]
    })

    // The same tag text is often boxed more than once over one spot, and a tag
    // can carry both a box and a subject highlight; without this those become a
    // stack of identical readings instead of the one chip they represent.
    const deduped = chips.filter(
      (chip, index) =>
        !chips.some(
          (other, otherIndex) =>
            otherIndex < index &&
            other.payload.label === chip.payload.label &&
            other.x < chip.x + chip.width &&
            chip.x < other.x + other.width &&
            other.y < chip.y + chip.height &&
            chip.y < other.y + other.height
        )
    )

    const normalized = normalizeChipSizes(deduped)

    const chipRectByBoxId = new Map<string, DisplayRect>()
    for (const chip of normalized) {
      if (chip.payload.source.kind === 'box') {
        chipRectByBoxId.set(chip.payload.source.box.id, {
          x: chip.x,
          y: chip.y,
          width: chip.width,
          height: chip.height,
        })
      }
    }

    return { chips: normalized, chipRectByBoxId }
  }, [anchors, detailTier, liveValues])
}
